import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link2, Trash2, UserMinus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { Insignia } from "@/componentes/ui/Insignia"

import type { Nodo } from "./tipos"

/**
 * El panel de una posición: quién la ocupa, de quién depende y a quién más reporta.
 *
 * ⚠ **«Depende de» es un selector y no sólo arrastrar** (WCAG 2.5.7). Arrastrar es el
 * atajo cómodo; quien navega con teclado, con un lector de pantalla o desde una
 * tableta necesita poder reorganizar igual.
 *
 * ⚠ **«Dejar vacante» es un botón aparte de elegir persona.** En un formulario, dejar
 * el selector en blanco y guardar es indistinguible de no haberlo tocado, y por eso el
 * PATCH lleva un `vaciar` explícito: sustituir a alguien tiene que ser una acción, no
 * la ausencia de una.
 *
 * ⚠ **Borrar avisa de cuántas posiciones se lleva.** La cascada existe para que
 * reorganizar no deje huérfanos, pero una pulsación no puede llevarse una rama entera
 * sin que quien la pulsa sepa el tamaño. Misma regla que en el Drive.
 */

type Cargo = { id: number; nombre: string }
type Persona = { id: number; nombres: string; apellidos: string }

export function PanelPosicion({
  nodo,
  nodos,
  puedeEditar,
  onCerrar,
}: {
  nodo: Nodo
  nodos: Nodo[]
  puedeEditar: boolean
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [nuevoSuperior, setNuevoSuperior] = useState("")

  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<Cargo[]>("/api/v1/cargos?solo_activos=true"),
  })
  const personas = useQuery({
    queryKey: ["personas", "para-organigrama"],
    // ⚠ `/personas` devuelve una PÁGINA (`{items, total}`), no una lista: leerla como
    // lista tumbaba la pantalla con «.map is not a function» (10/10).
    queryFn: async () => (await api.get<{ items: Persona[] }>("/api/v1/personas?limit=200")).items,
  })

  const refrescar = () =>
    clienteQuery.invalidateQueries({ queryKey: ["organigrama", "nodos", nodo.organigrama_id] })

  const editar = useMutation({
    mutationFn: (cambios: Record<string, unknown>) =>
      api.patch<Nodo>(`/api/v1/nodos/${nodo.id}`, cambios),
    onSuccess: refrescar,
  })

  const eliminar = useMutation({
    mutationFn: (cascada: boolean) =>
      api.delete(`/api/v1/nodos/${nodo.id}${cascada ? "?cascada=true" : ""}`),
    onSuccess: () => {
      refrescar()
      onCerrar()
    },
  })

  const agregarSuperior = useMutation({
    mutationFn: (superior_id: number) =>
      api.post(`/api/v1/nodos/${nodo.id}/superiores`, { superior_id }),
    onSuccess: () => {
      refrescar()
      setNuevoSuperior("")
    },
  })

  const quitarSuperior = useMutation({
    mutationFn: (superior_id: number) =>
      api.delete(`/api/v1/nodos/${nodo.id}/superiores/${superior_id}`),
    onSuccess: refrescar,
  })

  const estilo = {
    borderColor: "var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--fg)",
  }
  const otros = nodos.filter((n) => n.id !== nodo.id)
  const porId = new Map(nodos.map((n) => [n.id, n]))
  const error = [editar.error, eliminar.error, agregarSuperior.error].find(
    (e) => e instanceof ErrorApi,
  ) as ErrorApi | undefined

  return (
    <Cajon abierto titulo={nodo.cargo ?? "Posición"} onCerrar={onCerrar}>
      <div className="space-y-5">
        <div>
          <p className="text-sm">{nodo.vacante ? <em>Posición vacante</em> : nodo.ocupante}</p>
          {nodo.departamento && (
            <p className="mt-1">
              <Insignia tono="neutro">{nodo.departamento}</Insignia>
            </p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {error.message}
          </p>
        )}

        {puedeEditar && (
          <>
            <div>
              <label htmlFor="p-cargo" className="mb-1 block text-xs font-medium uppercase">
                Cargo
              </label>
              <select
                id="p-cargo"
                value={nodo.cargo_id}
                onChange={(e) => editar.mutate({ cargo_id: Number(e.target.value) })}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
                style={estilo}
              >
                {cargos.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="p-persona" className="mb-1 block text-xs font-medium uppercase">
                Ocupante
              </label>
              <div className="flex gap-2">
                <select
                  id="p-persona"
                  value={nodo.persona_id ?? ""}
                  onChange={(e) =>
                    e.target.value && editar.mutate({ persona_id: Number(e.target.value) })
                  }
                  className="h-9 min-w-0 flex-1 rounded-[var(--radius-control)] border px-2 text-sm"
                  style={estilo}
                >
                  <option value="">Sin asignar</option>
                  {personas.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombres} {p.apellidos}
                    </option>
                  ))}
                </select>
                {!nodo.vacante && (
                  <Boton
                    variante="fantasma"
                    onClick={() => editar.mutate({ vaciar: true })}
                    aria-label="Dejar la posición vacante"
                    title="Dejar vacante"
                  >
                    <UserMinus size={15} aria-hidden="true" />
                  </Boton>
                )}
              </div>
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                La misma persona puede ocupar varias posiciones.
              </p>
            </div>

            <div>
              <label htmlFor="p-padre" className="mb-1 block text-xs font-medium uppercase">
                Depende de
              </label>
              <select
                id="p-padre"
                value={nodo.padre_id ?? ""}
                onChange={(e) =>
                  editar.mutate({ padre_id: e.target.value ? Number(e.target.value) : null })
                }
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
                style={estilo}
              >
                <option value="">Nadie (es una de las cabezas)</option>
                {otros.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.cargo} {n.vacante ? "(vacante)" : `— ${n.ocupante}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase">
                <Link2 size={12} aria-hidden="true" className="inline" /> También reporta a
              </p>
              <p className="mb-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                La línea punteada del reporte matricial. El jefe directo no se repite aquí.
              </p>
              <ul className="mb-2 space-y-1">
                {nodo.reporta_tambien_a.map((id) => (
                  <li key={id} className="flex items-center gap-2 text-sm">
                    <span className="flex-1">{porId.get(id)?.cargo ?? `#${id}`}</span>
                    <button
                      type="button"
                      onClick={() => quitarSuperior.mutate(id)}
                      aria-label={`Quitar a ${porId.get(id)?.cargo ?? id}`}
                      style={{ color: "var(--destructive)" }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <select
                  aria-label="Añadir un jefe adicional"
                  value={nuevoSuperior}
                  onChange={(e) => setNuevoSuperior(e.target.value)}
                  className="h-9 min-w-0 flex-1 rounded-[var(--radius-control)] border px-2 text-sm"
                  style={estilo}
                >
                  <option value="">Elegir…</option>
                  {otros
                    .filter((n) => n.id !== nodo.padre_id && !nodo.reporta_tambien_a.includes(n.id))
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.cargo}
                      </option>
                    ))}
                </select>
                <Boton
                  variante="secundario"
                  onClick={() => nuevoSuperior && agregarSuperior.mutate(Number(nuevoSuperior))}
                  disabled={!nuevoSuperior || agregarSuperior.isPending}
                >
                  Añadir
                </Boton>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
              <Boton
                variante="fantasma"
                onClick={() => eliminar.mutate(false)}
                disabled={eliminar.isPending}
                style={{ color: "var(--destructive)" }}
              >
                <Trash2 size={15} aria-hidden="true" />
                Eliminar la posición
              </Boton>
              {eliminar.error instanceof ErrorApi &&
                typeof eliminar.error.detalles?.descendientes === "number" && (
                  <div className="mt-2">
                    <p className="text-sm" style={{ color: "var(--destructive)" }}>
                      Tiene {String(eliminar.error.detalles?.descendientes)} posiciones debajo.
                    </p>
                    <Boton
                      variante="peligro"
                      tamano="sm"
                      className="mt-2"
                      onClick={() => eliminar.mutate(true)}
                    >
                      Eliminarla con todo su equipo
                    </Boton>
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </Cajon>
  )
}
